import { z } from 'zod';
/** Bounded chart research only. Not an input contract for trading decisions. */
export const MICROTREND_COMPARISON_MAX_BARS = 2000;
export const microtrendComparisonDurationSchema = z.enum(['1m', '3m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d']);
/** Nominal sizes only; equity availability is bounded by the trading session. */
export const MICROTREND_COMPARISON_DURATION_SECONDS = {
    '1m': 60,
    '3m': 180,
    '5m': 300,
    '15m': 900,
    '30m': 1800,
    '60m': 3600,
    '120m': 7200,
    '180m': 10800,
    '240m': 14400,
    '1d': 86400
};
const finite = z.number().finite();
const timestamp = z.number().int().nonnegative();
export const microtrendComparisonConfigSchema = z.object({
    atrPeriod: z.number().int().min(1).max(200),
    reversalAtrMultiple: finite.min(0).max(20),
    minimumReversalPct: finite.gt(0).lt(100)
});
const statsSchema = z.object({
    cl: finite,
    bl: finite,
    ct: finite,
    cb: finite,
    bwl: finite,
    twl: finite,
    bp: finite,
    twp: finite,
    bwp: finite,
    cmp: finite,
    pc: finite,
    c: z.enum(['g', 'r']),
    ncb: finite,
    nct: finite
});
export const microtrendComparisonBarSchema = z
    .object({
    t: timestamp,
    o: finite.positive(),
    h: finite.positive(),
    l: finite.positive(),
    c: finite.positive(),
    v: finite.nonnegative(),
    s: statsSchema
})
    .refine((b) => b.h >= Math.max(b.o, b.c, b.l) && b.l <= Math.min(b.o, b.c), 'Invalid OHLC range');
export const microtrendComparisonSnapshotSchema = z.object({
    entityId: z.number().int().positive(),
    entityType: z.enum(['Asset', 'AssetIndex', 'Cryptocurrency']),
    duration: microtrendComparisonDurationSchema,
    shouldRemoveAfterHours: z.boolean(),
    bars: z.array(microtrendComparisonBarSchema).min(1).max(MICROTREND_COMPARISON_MAX_BARS),
    sessions: z.array(z.object({ id: z.string().min(1).max(80), start: timestamp, end: timestamp })).max(2100)
});
export const compareMicrotrendBarsInputSchema = z.object({
    snapshot: microtrendComparisonSnapshotSchema,
    cutoffCount: z.number().int().min(1).max(MICROTREND_COMPARISON_MAX_BARS),
    config: microtrendComparisonConfigSchema,
    persistedDaily: z.boolean().optional(),
    persistedRunId: z
        .string()
        .regex(/^[a-fA-F0-9]{64}$/)
        .optional()
});
const observationSchema = z.object({ sequence: timestamp, availableAt: timestamp });
const anchorSchema = z.object({
    sequence: timestamp,
    price: finite,
    basis: z.enum(['high', 'low']),
    barOpenedAt: timestamp,
    barClosedAt: timestamp,
    precedingAtr: finite,
    reversalDistance: finite
});
const metricsSchema = z.object({
    priceChange: finite,
    priceChangePct: finite,
    barIntervals: timestamp,
    includedBarCount: timestamp,
    elapsedSeconds: timestamp,
    returnPctPerBar: finite,
    returnPctPerElapsedMinute: finite,
    displacementAtr: finite.nullable(),
    speedAtrPerBar: finite.nullable(),
    directionalClosePathEfficiency: finite,
    maxInternalClosePullback: finite,
    maxInternalClosePullbackFraction: finite,
    volumeSum: finite,
    volumeMean: finite
});
const legSchema = z.object({
    id: z.string(),
    runFingerprint: z.string(),
    continuityId: z.string(),
    direction: z.enum(['up', 'down']),
    origin: anchorSchema,
    endpoint: anchorSchema,
    firstDetected: observationSchema,
    metrics: metricsSchema
});
const developingSchema = z.object({
    leg: legSchema,
    observedThrough: observationSchema,
    barsSinceExtreme: timestamp,
    ageBars: timestamp,
    currentClosePullback: finite,
    currentRetracementFraction: finite,
    tailVolume: finite
});
const researchPolicySchema = z.object({
    impulse: z.object({
        minimumDisplacementAtr: finite.nonnegative(),
        minimumSpeedAtrPerBar: finite.nonnegative(),
        minimumEfficiency: finite.min(0).max(1),
        maximumPullbackFraction: finite.nonnegative(),
        minimumBarIntervals: z.number().int().positive()
    }),
    minimumHistorySamples: z.number().int().positive(),
    maximumHistorySamples: z.number().int().positive(),
    maximumHistoryAgeSeconds: z.number().int().positive()
});
const researchLegObservationSchema = z.object({
    legId: z.string(),
    direction: z.enum(['up', 'down']),
    lifecycle: z.enum(['developing', 'confirmed', 'interrupted']),
    behavior: z.enum(['extending', 'retracing', 'holding']).nullable(),
    qualification: z.enum(['candidate', 'confirmedImpulse', 'criteriaNotMet', 'unavailable', 'interrupted']),
    criteria: z.array(z.string()),
    priceChangePct: finite,
    displacementAtr: finite.nullable(),
    speedAtrPerBar: finite.nullable(),
    efficiency: finite,
    currentRetracementFraction: finite.nullable(),
    history: z.object({
        frozenBefore: observationSchema,
        sampleCount: z.number().int().nonnegative(),
        oldestConfirmationAt: timestamp.nullable(),
        newestConfirmationAt: timestamp.nullable(),
        medianAbsoluteReturnPct: finite.nullable(),
        p90AbsoluteReturnPct: finite.nullable(),
        sizePercentile: finite.min(0).max(100).nullable()
    })
});
const researchObservationFrameSchema = z.object({
    inputIndex: z.number().int().nonnegative(),
    observedThrough: observationSchema,
    boundary: z.enum(['sessionEnd', 'missingData', 'dataRevision', 'analysisEnd']).nullable(),
    state: z.enum(['warmingUp', 'undetermined', 'developingUp', 'developingDown']),
    current: researchLegObservationSchema.nullable(),
    confirmed: researchLegObservationSchema.nullable(),
    interrupted: researchLegObservationSchema.nullable()
});
export const microtrendRustSnapshotSchema = z.object({
    algorithmVersion: z.literal('microtrend-v2.0.0'),
    runFingerprint: z.string(),
    state: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('warmingUp') }),
        z.object({ kind: z.literal('undetermined') }),
        z.object({ kind: z.literal('developingUp'), value: developingSchema }),
        z.object({ kind: z.literal('developingDown'), value: developingSchema })
    ]),
    confirmed: z.array(z.object({
        leg: legSchema,
        confirmed: observationSchema,
        confirmationDelayBars: timestamp,
        confirmationDelaySeconds: timestamp,
        confirmationPullback: finite,
        confirmationTailVolume: finite
    })),
    interrupted: z.array(z.object({
        developing: developingSchema,
        interruption: z.object({
            boundaryAt: timestamp,
            availableAt: timestamp,
            reason: z.enum(['sessionEnd', 'missingData', 'dataRevision', 'analysisEnd'])
        })
    })),
    observationVersion: z.enum(['microtrend-observations-v1', 'microtrend-observations-larger-history-v2']),
    observationPolicy: researchPolicySchema,
    observations: z.array(researchObservationFrameSchema).max(4000),
    diagnostics: z.array(z.object({
        sequence: timestamp,
        diagnostic: z.enum(['ambiguousSeed', 'outsideBarDeferred', 'unreachableReversalDistance', 'zeroOriginAtr'])
    }))
});
/** Predeclared sensitivity experiments, shared by chart and offline validation. Not trading defaults. */
export const MICROTREND_RESEARCH_PRESET_VERSION = 'microtrend-sensitivity-v1';
export const MICROTREND_RESEARCH_PRESETS = [
    { id: 'fine', label: 'Fine', config: { atrPeriod: 14, reversalAtrMultiple: 0.5, minimumReversalPct: 0.075 } },
    { id: 'standard', label: 'Standard', config: { atrPeriod: 14, reversalAtrMultiple: 1, minimumReversalPct: 0.15 } },
    { id: 'coarse', label: 'Coarse', config: { atrPeriod: 14, reversalAtrMultiple: 2, minimumReversalPct: 0.3 } }
];
const microtrendProviderRunSchema = z
    .object({
    entityId: z.string().min(1).max(128),
    entityType: z.enum(['Asset', 'AssetIndex', 'Cryptocurrency']),
    duration: microtrendComparisonDurationSchema,
    priceBasis: z.string().min(1).max(128),
    dataRevision: z.string().min(1).max(255),
    continuityPolicy: z.enum([
        'equityRegularSessions',
        'equityRegularSessionsAcrossSessions',
        'equityExtendedSegments',
        'continuous',
        'calendarBars'
    ]),
    availabilityMode: z.enum(['recordedAvailability', 'assumedBarCloseAvailability']),
    analysisStart: timestamp,
    config: microtrendComparisonConfigSchema,
    // Absence preserves the V1 run identity; never infer V2 from duration alone.
    observationPolicyVersion: z.enum(['microtrend-observations-v1', 'microtrend-observations-larger-history-v2']).optional()
})
    .refine((run) => run.observationPolicyVersion !== 'microtrend-observations-larger-history-v2' ||
    ['120m', '180m', '240m', '1d'].includes(run.duration), 'Larger-history v2 requires 120m, 180m, 240m or 1d');
const compositeMetricsSchema = z.object({
    priceChange: finite,
    priceChangePct: finite,
    barIntervals: timestamp,
    elapsedSeconds: timestamp,
    displacementAtr: finite.nullable(),
    speedAtrPerBar: finite.nullable(),
    maximumInternalRetracement: finite.nonnegative(),
    maximumInternalRetracementFraction: finite.nonnegative()
});
const compositePathSchema = z.object({
    directionalClosePathEfficiency: finite.nonnegative(),
    directionalCloseFraction: finite.nonnegative(),
    rangeEfficiency: finite.nonnegative().nullable(),
    maximumBarRangeFraction: finite.nonnegative(),
    maximumOpenGapFraction: finite.nonnegative(),
    endpointRejectionFraction: finite.nonnegative()
});
export const microtrendPersistedResearchViewSchema = z.object({
    version: z.literal('microtrend-research-view-v1'),
    runId: z.string().regex(/^[a-fA-F0-9]{64}$/),
    run: microtrendProviderRunSchema,
    publication: z.object({
        revision: timestamp,
        inputCount: timestamp,
        coveredThrough: timestamp,
        finalizedCount: timestamp,
        compositeFinalizedCount: timestamp
    }),
    cutoff: timestamp,
    totalFinalizedBaseMoves: timestamp,
    totalFinalizedComposites: timestamp,
    impulses: z
        .array(z.object({
        id: z.string().min(1),
        lifecycle: z.enum(['finalized', 'provisional']),
        direction: z.enum(['up', 'down']),
        origin: anchorSchema,
        endpoint: anchorSchema,
        geometryFirstKnownAt: timestamp,
        availableAt: timestamp,
        finalizedAt: timestamp.nullable(),
        finalizationReason: z.enum(['retracementLimitExceeded', 'sessionEnd', 'missingData', 'continuityChanged']).nullable(),
        constituentLegIds: z.array(z.string().min(1)).min(3).max(21),
        metrics: compositeMetricsSchema,
        path: compositePathSchema.nullable(),
        fibonacci: z
            .array(z.object({
            retracementRatio: finite.min(0).max(1),
            price: finite.positive()
        }))
            .length(7)
    }))
        .max(201)
});
/** One read-only research owner, distinct from the retained October pilot and trading bindings. */
export const AAPL_DAILY_MICROTREND_OWNER = {
    assetId: 12,
    startDate: '2023-01-03',
    sourceRevision: 'aapl-daily-owner-20261014',
    runId: '77a986f446fcafe02bcb3ba58341de12f7f4a0a1cfeddf54e7e76100fb260d82',
    config: { atrPeriod: 14, reversalAtrMultiple: 1, minimumReversalPct: 0.15 }
};
/** Bounded read-only daily cohort. These identities never activate trading bindings. */
export const DAILY_MICROTREND_RESEARCH_OWNERS = [
    { ...AAPL_DAILY_MICROTREND_OWNER, symbol: 'AAPL' },
    {
        assetId: 4166,
        symbol: 'MSFT',
        startDate: '2023-01-03',
        sourceRevision: 'daily-cohort-20261015',
        runId: '6a742108910d8f04ab115780f98fea7b3429b7bf3668f810e2db4be7a6219451',
        config: { atrPeriod: 14, reversalAtrMultiple: 1, minimumReversalPct: 0.15 }
    },
    {
        assetId: 3537,
        symbol: 'JPM',
        startDate: '2023-01-03',
        sourceRevision: 'daily-cohort-20261015',
        runId: 'b8dd26fedf16380d0cf86903210515d1f38b3d451d83017fe9f269a5d8cf3408',
        config: { atrPeriod: 14, reversalAtrMultiple: 1, minimumReversalPct: 0.15 }
    }
];
//# sourceMappingURL=MicrotrendComparison.js.map